
import CatalogoDigitalProductsTab from "../components/CatalogoDigitalProductsTab.vue";
import CatalogoDigitalSettingsTab from "../components/CatalogoDigitalSettingsTab.vue";
import {
  CATALOG_THEME_PRESETS,
  DEFAULT_CATALOG_THEME,
  extractCatalogCollection,
  isCatalogConfigurationDraftDirty,
  isValidHexColor,
  normalizeCatalogConfiguration,
  normalizeCatalogProduct,
  normalizeCatalogPromotion,
  normalizeDecimal,
  promotionStatus,
  slugifyCatalogName,
} from "../catalogoDigitalConfig";
import {
  deleteCatalogPromotion,
  getCatalogConfiguration,
  getCatalogProducts,
  getCatalogPromotions,
  saveCatalogConfiguration,
  saveCatalogPromotion,
} from "../services/catalogoDigitalApi";

const createPromotionForm = () => ({
  id: null,
  id_produto: null,
  nome: "",
  descricao: "",
  tipo: "percentual",
  valor: "",
  inicio_em: "",
  fim_em: "",
  ativo: true,
  destaque: false,
});

const toLocalInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const toIsoValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export default {
  name: "CatalogoDigitalAdmin",
  components: {
    CatalogoDigitalProductsTab,
    CatalogoDigitalSettingsTab,
  },
  data() {
    return {
      activeTab: "appearance",
      loadingConfiguration: false,
      loadingProducts: false,
      loadingPromotions: false,
      savingConfiguration: false,
      savingPromotion: false,
      deletingPromotionId: null,
      configuration: normalizeCatalogConfiguration(),
      configurationBaseline: normalizeCatalogConfiguration(),
      configurationErrors: {},
      products: [],
      promotions: [],
      promotionForm: createPromotionForm(),
      promotionErrors: {},
      assets: {
        logoFile: null,
        logoPreview: "",
        coverFile: null,
        coverPreview: "",
      },
      themePresets: CATALOG_THEME_PRESETS,
      themeColors: [
        { key: "cor_primaria", label: "Cor principal" },
        { key: "cor_secundaria", label: "Cor de destaque" },
        { key: "cor_texto", label: "Textos" },
        { key: "cor_fundo", label: "Fundo" },
      ],
      previewProducts: [
        {
          name: "Produto essencial",
          price: "R$ 49,90",
          tag: "Mais vendido",
          emoji: "✦",
          color: "#F1ECFF",
        },
        {
          name: "Seleção especial",
          price: "R$ 79,90",
          tag: "Novidade",
          emoji: "◌",
          color: "#FFF0E7",
        },
      ],
    };
  },
  computed: {
    configurationDirty() {
      return isCatalogConfigurationDraftDirty(
        this.configuration,
        this.configurationBaseline,
        Boolean(this.assets.logoFile || this.assets.coverFile)
      );
    },
    previewLogo() {
      return this.assets.logoPreview || this.configuration.logo_url;
    },
    previewCover() {
      return this.assets.coverPreview || this.configuration.capa_url;
    },
    coverPreviewStyle() {
      if (this.previewCover) {
        return {
          backgroundImage: `url(${this.previewCover})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        };
      }

      return {
        background: `linear-gradient(135deg, ${this.configuration.tema.cor_primaria}, ${this.configuration.tema.cor_secundaria})`,
      };
    },
    storeInitials() {
      const words = String(this.configuration.nome || "Sua Loja")
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2);
      return words.map((word) => word.charAt(0).toUpperCase()).join("") || "SL";
    },
    buttonTextColor() {
      const hex = this.configuration.tema.cor_primaria.replace("#", "");
      if (hex.length !== 6) return "#FFFFFF";
      const red = parseInt(hex.slice(0, 2), 16);
      const green = parseInt(hex.slice(2, 4), 16);
      const blue = parseInt(hex.slice(4, 6), 16);
      return red * 0.299 + green * 0.587 + blue * 0.114 > 170
        ? "#172033"
        : "#FFFFFF";
    },
    activePromotionsCount() {
      return this.promotions.filter((item) => promotionStatus(item) === "ativa")
        .length;
    },
    scheduledPromotionsCount() {
      return this.promotions.filter(
        (item) => promotionStatus(item) === "agendada"
      ).length;
    },
  },
  created() {
    this.loadConfiguration();
    this.loadProducts();
    this.loadPromotions();
    window.addEventListener(
      "beforeunload",
      this.handleConfigurationBeforeUnload
    );
  },
  beforeDestroy() {
    window.removeEventListener(
      "beforeunload",
      this.handleConfigurationBeforeUnload
    );
    this.revokePreview("logo");
    this.revokePreview("cover");
  },
  beforeRouteLeave(to, from, next) {
    const productsTab = this.$refs.productsTab;
    const productDraftDiscardApproved = Boolean(
      productsTab && productsTab.consumeRouteLeaveApproval()
    );
    if (productsTab && productsTab.saving) {
      this.$toast.warning("Aguarde a publicação dos produtos terminar.");
      next(false);
      return;
    }
    if (
      productsTab &&
      productsTab.hasDrafts &&
      !productDraftDiscardApproved &&
      !window.confirm(
        "Existem alterações não publicadas. Deseja descartá-las e sair desta tela?"
      )
    ) {
      next(false);
      return;
    }
    if (this.savingConfiguration) {
      this.$toast.warning("Aguarde a publicação da aparência terminar.");
      next(false);
      return;
    }
    if (
      this.configurationDirty &&
      !window.confirm(
        "Existem alterações de aparência não publicadas. Deseja descartá-las e sair desta tela?"
      )
    ) {
      next(false);
      return;
    }
    next();
  },
  methods: {
    handleConfigurationBeforeUnload(event) {
      if (!this.configurationDirty || this.savingConfiguration) return;
      event.preventDefault();
      event.returnValue = "";
    },
    async handleProductsPublished(data) {
      const preserveAppearanceDraft = this.configurationDirty;
      const publishedConfiguration = data && data.configuracao;

      if (!preserveAppearanceDraft && publishedConfiguration) {
        const normalized = normalizeCatalogConfiguration(
          publishedConfiguration
        );
        this.configuration = normalized;
        this.configurationBaseline = normalizeCatalogConfiguration(normalized);
      }

      const refreshes = [this.loadProducts(), this.loadPromotions()];
      if (!preserveAppearanceDraft && !publishedConfiguration) {
        refreshes.push(this.loadConfiguration());
      }
      await Promise.all(refreshes);
    },
    async loadConfiguration() {
      this.loadingConfiguration = true;
      try {
        const data = await getCatalogConfiguration();
        const normalized = normalizeCatalogConfiguration(data || {});
        this.configuration = normalized;
        this.configurationBaseline = normalizeCatalogConfiguration(normalized);
      } catch (error) {
        this.notifyError(error, "Não foi possível carregar o catálogo.");
      } finally {
        this.loadingConfiguration = false;
      }
    },
    async loadProducts() {
      this.loadingProducts = true;
      try {
        const data = await getCatalogProducts();
        this.products = extractCatalogCollection(data, "produtos").map(
          normalizeCatalogProduct
        );
      } catch (error) {
        this.notifyError(error, "Não foi possível carregar os produtos.");
      } finally {
        this.loadingProducts = false;
      }
    },
    async loadPromotions() {
      this.loadingPromotions = true;
      try {
        const data = await getCatalogPromotions();
        this.promotions = extractCatalogCollection(data, "promocoes").map(
          normalizeCatalogPromotion
        );
      } catch (error) {
        this.notifyError(error, "Não foi possível carregar as promoções.");
      } finally {
        this.loadingPromotions = false;
      }
    },
    fillSlugFromName() {
      if (!this.configuration.slug) {
        this.configuration.slug = slugifyCatalogName(this.configuration.nome);
      }
    },
    normalizeSlug() {
      this.configuration.slug = slugifyCatalogName(this.configuration.slug);
    },
    fieldError(field) {
      const value = this.configurationErrors[field];
      return Array.isArray(value) ? value[0] : value || "";
    },
    promotionError(field) {
      const value = this.promotionErrors[field];
      return Array.isArray(value) ? value[0] : value || "";
    },
    validateConfiguration() {
      const errors = {};
      if (!this.configuration.nome.trim())
        errors.nome = "Informe o nome da loja.";
      if (!this.configuration.slug.trim())
        errors.slug = "Informe o endereço do catálogo.";
      this.themeColors.forEach((color) => {
        if (!isValidHexColor(this.configuration.tema[color.key])) {
          errors.tema = "Revise as cores do tema.";
        }
      });
      this.configurationErrors = errors;
      return Object.keys(errors).length === 0;
    },
    validateThemeColor(key) {
      if (!isValidHexColor(this.configuration.tema[key])) {
        this.configuration.tema[key] = DEFAULT_CATALOG_THEME[key];
        this.$toast.warning("Use uma cor hexadecimal com seis dígitos.");
      }
    },
    async saveConfiguration() {
      this.normalizeSlug();
      if (!this.validateConfiguration()) {
        this.$toast.error("Revise os campos destacados antes de publicar.");
        return;
      }

      this.savingConfiguration = true;
      const payload = new FormData();
      ["nome", "slug", "descricao", "logo_url", "capa_url"].forEach((key) => {
        payload.append(key, this.configuration[key] || "");
      });
      payload.append("ativo", this.configuration.ativo ? "1" : "0");
      payload.append("tema", JSON.stringify(this.configuration.tema));
      if (this.assets.logoFile) payload.append("logo", this.assets.logoFile);
      if (this.assets.coverFile) payload.append("capa", this.assets.coverFile);

      try {
        const data = await saveCatalogConfiguration(payload);
        this.configuration = normalizeCatalogConfiguration(
          data && data.configuracao
            ? data.configuracao
            : data || this.configuration
        );
        this.configurationBaseline = normalizeCatalogConfiguration(
          this.configuration
        );
        this.clearAsset("logo");
        this.clearAsset("cover");
        this.configurationErrors = {};
        this.$toast.success("Catálogo atualizado com sucesso.");
      } catch (error) {
        this.configurationErrors =
          error.errors || (error.response && error.response.data.errors) || {};
        this.notifyError(error, "Não foi possível salvar o catálogo.");
      } finally {
        this.savingConfiguration = false;
      }
    },
    selectAsset(type, event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      if (!/^image\/(png|jpeg|webp)$/.test(file.type)) {
        this.$toast.error("Escolha uma imagem PNG, JPG ou WebP.");
        event.target.value = "";
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.$toast.error("A imagem deve ter no máximo 5 MB.");
        event.target.value = "";
        return;
      }

      const isLogo = type === "logo";
      this.revokePreview(type);
      this.assets[isLogo ? "logoFile" : "coverFile"] = file;
      this.assets[isLogo ? "logoPreview" : "coverPreview"] =
        URL.createObjectURL(file);
    },
    revokePreview(type) {
      const key = type === "logo" ? "logoPreview" : "coverPreview";
      if (this.assets[key] && this.assets[key].startsWith("blob:")) {
        URL.revokeObjectURL(this.assets[key]);
      }
    },
    clearAsset(type) {
      const isLogo = type === "logo";
      this.revokePreview(type);
      this.assets[isLogo ? "logoFile" : "coverFile"] = null;
      this.assets[isLogo ? "logoPreview" : "coverPreview"] = "";
      const input = this.$refs[isLogo ? "logoInput" : "coverInput"];
      if (input) input.value = "";
    },
    applyPreset(preset) {
      this.configuration.tema = { ...preset.tema };
    },
    isPresetSelected(preset) {
      return Object.keys(preset.tema).every(
        (key) => preset.tema[key] === this.configuration.tema[key]
      );
    },
    openPublicCatalog() {
      if (this.configuration.public_url) {
        window.open(this.configuration.public_url, "_blank", "noopener");
      }
    },
    validatePromotion() {
      const errors = {};
      const value = normalizeDecimal(this.promotionForm.valor);
      if (!this.promotionForm.id_produto)
        errors.id_produto = "Selecione um produto.";
      if (!this.promotionForm.nome.trim())
        errors.nome = "Informe o nome da campanha.";
      if (!value || Number(value) <= 0)
        errors.valor = "Informe um valor maior que zero.";
      if (this.promotionForm.tipo === "percentual" && Number(value) > 100) {
        errors.valor = "O percentual deve ser de no máximo 100%.";
      }
      const start = toIsoValue(this.promotionForm.inicio_em);
      const end = toIsoValue(this.promotionForm.fim_em);
      if (start && end && new Date(start) >= new Date(end)) {
        errors.fim_em = "O término deve ser posterior ao início.";
      }
      this.promotionErrors = errors;
      return Object.keys(errors).length === 0;
    },
    async savePromotion() {
      if (!this.validatePromotion()) {
        this.$toast.error("Revise os dados da promoção.");
        return;
      }

      this.savingPromotion = true;
      const payload = {
        id: this.promotionForm.id || undefined,
        nome: this.promotionForm.nome.trim(),
        descricao: this.promotionForm.descricao.trim(),
        tipo: this.promotionForm.tipo,
        valor: normalizeDecimal(this.promotionForm.valor),
        escopo: "produto",
        id_produto: this.promotionForm.id_produto,
        inicio_em: toIsoValue(this.promotionForm.inicio_em),
        fim_em: toIsoValue(this.promotionForm.fim_em),
        ativo: this.promotionForm.ativo,
        destaque: this.promotionForm.destaque,
        ordem: 0,
      };

      try {
        await saveCatalogPromotion(payload);
        this.$toast.success(
          this.promotionForm.id
            ? "Promoção atualizada com sucesso."
            : "Promoção agendada com sucesso."
        );
        this.resetPromotionForm();
        await this.loadPromotions();
      } catch (error) {
        this.promotionErrors =
          error.errors || (error.response && error.response.data.errors) || {};
        this.notifyError(error, "Não foi possível salvar a promoção.");
      } finally {
        this.savingPromotion = false;
      }
    },
    editPromotion(promotion) {
      this.promotionForm = {
        id: promotion.id,
        id_produto: promotion.id_produto,
        nome: promotion.nome,
        descricao: promotion.descricao,
        tipo: promotion.tipo,
        valor: promotion.valor,
        inicio_em: toLocalInputValue(promotion.inicio_em),
        fim_em: toLocalInputValue(promotion.fim_em),
        ativo: promotion.ativo,
        destaque: promotion.destaque,
      };
      this.promotionErrors = {};
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    resetPromotionForm() {
      this.promotionForm = createPromotionForm();
      this.promotionErrors = {};
    },
    async removePromotion(promotion) {
      const confirmed = window.confirm(
        `Excluir a promoção “${promotion.nome}”? Esta ação não pode ser desfeita.`
      );
      if (!confirmed) return;

      this.deletingPromotionId = promotion.id;
      try {
        await deleteCatalogPromotion(promotion.id);
        this.promotions = this.promotions.filter(
          (item) => item.id !== promotion.id
        );
        if (this.promotionForm.id === promotion.id) this.resetPromotionForm();
        this.$toast.success("Promoção excluída com sucesso.");
      } catch (error) {
        this.notifyError(error, "Não foi possível excluir a promoção.");
      } finally {
        this.deletingPromotionId = null;
      }
    },
    promotionProduct(promotion) {
      return (
        this.products.find(
          (product) => String(product.id) === String(promotion.id_produto)
        ) || {
          descricao: promotion.produto_nome || "Produto do catálogo",
          imagem: promotion.produto_imagem || "",
        }
      );
    },
    promotionStatus,
    promotionStatusLabel(promotion) {
      return {
        ativa: "Ativa agora",
        agendada: "Agendada",
        encerrada: "Encerrada",
        inativa: "Inativa",
      }[promotionStatus(promotion)];
    },
    promotionValueLabel(promotion) {
      return promotion.tipo === "percentual"
        ? `${Number(promotion.valor).toLocaleString("pt-BR")}% OFF`
        : `${this.formatMoney(promotion.valor)} de desconto`;
    },
    promotionPeriodLabel(promotion) {
      if (!promotion.inicio_em && !promotion.fim_em)
        return "Sem período definido";
      const start = promotion.inicio_em
        ? this.formatDate(promotion.inicio_em)
        : "agora";
      const end = promotion.fim_em
        ? this.formatDate(promotion.fim_em)
        : "sem fim";
      return `${start} → ${end}`;
    },
    formatDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "Data inválida";
      return new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    notifyError(error, fallback) {
      const message =
        (error &&
          error.response &&
          error.response.data &&
          error.response.data.message) ||
        (error && error.message) ||
        fallback;
      this.$toast.error(message || fallback);
    },
  },
};
